import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { GiftIcon, CheckCircle2Icon } from 'lucide-react';
function BonusBullet({
  children
}: {
  children: React.ReactNode;
}) {
  return <li className="flex items-start gap-3" data-id="element-1">
      <CheckCircle2Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
      color: '#D4A017'
    }} aria-hidden="true" data-id="element-2" />
      <span className="font-body text-base leading-relaxed" style={{
      color: 'rgba(250, 243, 232, 0.8)'
    }} data-id="element-3">
        {children}
      </span>
    </li>;
}
function BonusCard({
  number,
  title,
  subtitle,
  intro,
  bullets,
  closingLine,
  imageSrc,
  imageAlt,
  imageOnRight,
  delay
}: {
  number: number;
  title: string;
  subtitle: string;
  intro: string;
  bullets: string[];
  closingLine: string;
  imageSrc: string;
  imageAlt: string;
  imageOnRight: boolean;
  delay: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 40
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay
  }} data-id="element-4">
      <div className={`flex flex-col ${imageOnRight ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 md:gap-12 items-center`} data-id="element-5">
        {/* Content side */}
        <div className="flex-1 min-w-0" data-id="element-6">
          {/* Label */}
          <div className="flex items-center gap-2 mb-4" data-id="element-7">
            <GiftIcon className="w-5 h-5" style={{
            color: '#D4A017'
          }} aria-hidden="true" data-id="element-8" />
            <span className="font-body text-sm font-bold uppercase tracking-widest" style={{
            color: '#D4A017'
          }} data-id="element-9">
              Bono #{number}
            </span>
          </div>

          {/* Title */}
          <h3 className="font-serif-display font-bold text-2xl md:text-3xl leading-snug mb-2" style={{
          color: '#FAF3E8'
        }} data-id="element-10">
            {title}
          </h3>

          {/* Subtitle / value */}
          <p className="font-body text-base font-semibold mb-5" style={{
          color: '#D4A017'
        }} data-id="element-11">
            {subtitle}
          </p>

          {/* Intro */}
          <p className="font-body text-base leading-relaxed mb-6" style={{
          color: 'rgba(250, 243, 232, 0.75)'
        }} data-id="element-12">
            {intro}
          </p>

          {/* Bullets */}
          <ul className="space-y-3 mb-6" role="list" data-id="element-13">
            {bullets.map((bullet, i) => <BonusBullet key={i} data-id="element-14">{bullet}</BonusBullet>)}
          </ul>

          {/* Closing line */}
          <p className="font-serif-display italic text-base leading-relaxed mb-5" style={{
          color: 'rgba(250, 243, 232, 0.55)'
        }} data-id="element-15">
            {closingLine}
          </p>

          {/* Value badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg" style={{
          border: '1px solid rgba(212, 160, 23, 0.4)',
          backgroundColor: 'rgba(212, 160, 23, 0.08)'
        }} data-id="element-16">
            <GiftIcon className="w-4 h-4" style={{
            color: '#D4A017'
          }} aria-hidden="true" data-id="element-17" />
            <span className="font-body text-sm font-bold" style={{
            color: '#D4A017'
          }} data-id="element-18">
              Valor: $47 — Tuyo gratis hoy
            </span>
          </div>
        </div>

        {/* Image side */}
        <div className="flex-shrink-0 w-full md:w-[320px] lg:w-[380px]" data-id="element-19">
          <img src={imageSrc} alt={imageAlt} className="w-full rounded-xl shadow-2xl" data-id="element-20" />
        </div>
      </div>
    </motion.div>;
}
export function BonusSection() {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full" style={{
    backgroundColor: '#0B1A2E'
  }} data-id="element-21">
      {/* Top stitch */}
      <div style={{
      borderTop: '3px dashed #D4A017',
      opacity: 0.3
    }} data-id="element-22" />

      <div className="max-w-5xl mx-auto px-6 py-14 md:py-20" data-id="element-23">
        {/* Section header */}
        <motion.div ref={headerRef} className="text-center mb-14" initial={{
        opacity: 0,
        y: 30
      }} animate={headerInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} data-id="element-24">
          <div className="flex items-center justify-center gap-2 mb-4" data-id="element-25">
            <GiftIcon className="w-6 h-6" style={{
            color: '#D4A017'
          }} aria-hidden="true" data-id="element-26" />
            <p className="font-body text-sm uppercase tracking-widest" style={{
            color: '#D4A017'
          }} data-id="element-27">
              Bonos incluidos
            </p>
          </div>
          <h2 className="font-serif-display font-bold text-3xl md:text-4xl lg:text-5xl" style={{
          color: '#FAF3E8'
        }} data-id="element-28">
            Pero eso no es todo...
          </h2>
        </motion.div>

        {/* Bonus 1 */}
        <BonusCard number={1} title="Kit Anti-Errores de Alteraciones (Cero Pánico)" subtitle="Valor real: $47 — Tuyo gratis hoy" intro="Este es tu seguro para empezar sin miedo. Antes de coser cualquier prenda, vas a saber exactamente qué revisar, qué riesgos existen y cómo evitar los errores que más tiempo y dinero cuestan. Sin tecnicismos. Sin pánico." bullets={['Diagnóstico Express (2 minutos): detecta si la prenda es segura antes de tocarla con la máquina', 'Los 12 errores más comunes en alteraciones y su solución inmediata (ruedo torcido, zipper ondulado, tela fruncida y más)', 'Pruebas rápidas antes de coser para no arriesgar la prenda del cliente', 'Control de calidad en 60 segundos antes de entregar', 'Plan de rescate: qué decirle al cliente si algo salió mal (guión corto, sin perder autoridad)', 'Checklist imprimible "Antes de Coser" (pégala cerca de tu máquina)', 'Checklist imprimible "Antes de Entregar" (para que nada se te escape)', 'Plantilla de Tarjeta de Cuidados lista para imprimir y entregar con cada prenda']} closingLine="Porque no hay nada peor que entregar algo que no quedó bien. Y no hay nada mejor que saber, desde el principio, que tienes todo bajo control." imageSrc="https://cdn.magicpatterns.com/uploads/vTskndVjbsRt78ZaTuWoLr/heylloyd-image-1771560840108.png" imageAlt="Kit Anti-Errores de Alteraciones — Portada del bono digital con checklists y herramientas" imageOnRight={true} delay={0} data-id="element-29" />

        {/* Stitch divider */}
        <hr className="stitch-divider my-16 md:my-20" data-id="element-30" />

        {/* Bonus 2 */}
        <BonusCard number={2} title="Scripts de Confianza: Cómo Hablar con Clientes (Sin Pena)" subtitle="Valor real: $47 — Tuyo gratis hoy" intro='Coser bien no es suficiente si cuando te preguntan "¿cuánto cobras?" te tiembla la voz. Este bono existe para ese momento exacto. Vas a tener mensajes listos para copiar y pegar en WhatsApp, texto o en persona. Sin improvisar. Sin sonar insegura. Sin regalar tu trabajo.' bullets={['6 scripts para responder "¿cuánto cobras?" en diferentes situaciones (precio directo, con rango, urgente, cliente que quiere descuento)', 'Scripts para pedir fotos y medidas sin sonar exigente', 'Scripts para agendar y confirmar entrega sin perseguir al cliente', 'Scripts para poner límites con respeto y sin culpa', 'Scripts anti-quejas: cómo responder si algo no quedó como esperaban, sin pelear y sin perder autoridad', 'Guión de 30 segundos para hablar en persona y sonar profesional desde el primer día', 'Hoja de personalización para ajustar cada script a tu tono y estilo', 'Pack "copiar y pegar" en texto plano para guardar en el teléfono y usar hoy mismo']} closingLine="Porque el dinero que pierdes no siempre es por coser mal. A veces es por no saber cómo pedir lo que mereces." imageSrc="https://cdn.magicpatterns.com/uploads/j6urJh5Nrrw86vEKjBZ3Xb/heylloyd-image-1771560901278.png" imageAlt="Scripts de Confianza — Portada del bono digital con mensajes listos para copiar y pegar" imageOnRight={false} delay={0} data-id="element-31" />

        {/* Stitch divider */}
        <hr className="stitch-divider my-16 md:my-20" data-id="element-32" />

        {/* Bonus 3 */}
        <BonusCard number={3} title="Mini-Brand Express: Tu 'Sastrería en Casa' en 30 Minutos" subtitle="Valor real: $47 — Tuyo gratis hoy" intro='No necesitas un taller. No necesitas un logo caro. No necesitas nada sofisticado. Solo necesitas verte confiable. Porque cuando una clienta ve orden, entrega limpia y un pequeño sistema... automáticamente piensa: "Esta mujer se toma esto en serio." Y ahí es cuando llegan más recomendaciones y menos regateo.' bullets={['Setup Express: cómo convertir tu mesa de comedor en una estación pro en 30 minutos con lo que ya tienes', 'Sistema Anti-Confusión: 3 estados (Por Hacer / Listo / Entregado) para que nunca mezcles prendas de clientes diferentes', 'Lista de compra mínima ordenada por prioridad (sin gastar de más)', 'Kit de entrega en 60 segundos: dobla, empaca y entrega como boutique', 'Plantilla: Etiqueta de prenda (lista para imprimir, para que nunca confundas nombre/fecha/precio)', 'Plantilla: Tarjeta de agradecimiento (para que cada entrega genere una recomendación)', 'Plantilla: Cartelito de horarios (para que no lleguen sin avisar)', 'Checklist "Mi estación pro en 10 pasos" (imprimible)', 'Checklist "Entrega que se ve profesional" (imprimible)', 'Mini guía de fotos para que tu trabajo se vea caro en redes sin ser fotógrafa']} closingLine="Porque la primera impresión no la das cuando coses. La das cuando entregas." imageSrc="https://cdn.magicpatterns.com/uploads/khLLVVyxRMyZ3mk9mdeZJy/heylloyd-image-1771560712277.png" imageAlt="Mini-Brand Express — Portada del bono digital con estación pro y plantillas de entrega" imageOnRight={true} delay={0} data-id="element-33" />
      </div>

      {/* Bottom stitch */}
      <div style={{
      borderTop: '3px dashed #D4A017',
      opacity: 0.3
    }} data-id="element-34" />
    </section>;
}